import * as React        from "react";
import {cx}              from "emotion";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faCheck}         from '@fortawesome/free-solid-svg-icons';

import "./security-question-modal.scss";

import {cs}             from "@Common/react/chain-services";
import {consumeContext} from "@Common/react/context";
import {ComponentLink}  from "@Common/react/cst2";
import {Load2}          from "@Common/react/load2";
import {scopedState}    from "@Common/react/scoped-state";
import {UseState}       from "@Common/react/use-state";
import {ModalService3}  from "@Common/ui-components/modal-service-3/modal-service-3";
import {Select2}        from "@Common/ui-components/select-2/select-2";
import {PasswordInput}  from "@Common/ui-components/password-input/password-input";

import {ModalLayout} from "@Xpresso/routes/common/modal-layout/modal-layout";

export const SecurityQuestionModal = ({
    next: rootNext,
    title,
    onSave,
    urlParam
}: {
    next: ComponentLink<typeof ModalService3<any>, "rootNext">,
    title: string,
    onSave: (updateFields: any) => any,
    urlParam: string
}) => cs(
    consumeContext("session"),
    consumeContext("apis"),
    ["userProfile", ({apis, session}, next) => Load2({
        _key: JSON.stringify({...session.value}),
        fetch: async () => apis.next.userProfile.getUserProfile(),
        next,
    })],
    ["uploading", (_, next) => UseState({next})],
    ["modal", (_, next) => ModalService3({
        xdQaName: "Security Questions Modal",
        width: 600,
        next,
        urlParam,
        rootNext,
    })],
    ["state", ({userProfile}, next) => userProfile.value && UseState({
        initValue: defaultState({userProfile}),
        next
    })],
    ["validation", ({state}, next) => {
        const secretAnswerLength = state.value?.inputs?.secretAnswer?.length >= 6;
        const secretAnswer2Length = state.value?.inputs?.secretAnswer2?.length >= 6;
        const questionsDoNotMatch = !questionsMatch({state});
        const answersDoNotMatch = !answersMatch({state});

        return next({
            secretAnswerLength,
            secretAnswer2Length,
            questionsDoNotMatch,
            answersDoNotMatch,
            full: secretAnswerLength && secretAnswer2Length && questionsDoNotMatch && answersDoNotMatch
        });
    }],
    ({uploading, state, modal, userProfile, validation}, next) => ModalLayout({
        title,
        content: next(),
        buttons: [
            {
                label: "Cancel",
                onClick: () => {
                    state.onChange(defaultState({userProfile}));
                    modal.close();
                },
            },
            {
                label: "Save",
                disabled: uploading.value || !validation.full || missingData({state}),
                primary: true,
                onClick: async () => {
                    uploading.onChange(true);
                    const result = await onSave(state.value.inputs);
                    if (result?.errorMessage) {
                        alert(result.errorMessage);
                    } else if (result?.errorInfo) {
                        const errors = JSON.parse(result.errorInfo);
                        errors.map((error) => {
                            scopedState(state, ['errors', error.mapping]).onChange(error.message)
                        });
                    } else {
                        await userProfile.reload();
                        modal.close();
                    }
                    uploading.onChange(false);
                },
            },
        ],
    }),
    ({state, validation}) => (
        <div className="security-question-modal-t0d">
            <div className="helper" xd-qa-type="label" xd-qa-name="Info">
                Select two security questions below. These questions will help us verify your identity should you forget your password.
            </div>
            <div className="question-card">
                <div className="label" xd-qa-type="label" xd-qa-name="Security Question #1">Security Question #1</div>
                {QuestionSelect({inputProps:{
                    tabIndex: 101,
                    value: scopedState(state, ['inputs', "secretQuestion"]).value,
                    isSelected: (i) => i.value === scopedState(state, ['inputs', "secretQuestion"]).value,
                    onSelect: (i) => {
                        scopedState(state, ['inputs', "secretQuestion"]).onChange(i?.value ?? "");
                        scopedState(state, ['errors', "secretQuestion"]).onChange(false);
                    },
                    xdQaName: "Security Questions #1",
                }})}
                {state.value.errors.secretQuestion && (
                    <div className="errorMessage">{state.value.errors.secretQuestion}</div>
                )}
                {PasswordInput({inputProps:{
                    tabIndex: 102,
                    placeholder: "Answer",
                    value: scopedState(state, ['inputs', "secretAnswer"]).value, 
                    onChange: (e) => {
                        let value = e.target.value; 
                        scopedState(state, ['inputs', "secretAnswer"]).onChange(value);
                        scopedState(state, ['errors', "secretAnswer"]).onChange(false);
                    },
                    className: "answer",
                    maxLength: 50,
                    "xd-qa-type": "textbox",
                    "xd-qa-name": "Security Answer #1",
                    autoComplete: "new-password",
                }})}
                {state.value.errors.secretAnswer && (
                    <div className="errorMessage">{state.value.errors.secretAnswer}</div>
                )}
                <div className="validation-line">
                    {validation.secretAnswerLength ? (
                        <div className="validation-icon"><FontAwesomeIcon className={"icon"} icon={faCheck} xd-qa-type="icon" xd-qa-name="Answers must be at least 6 characters check icon #1"/></div>
                    ) : (
                        <div className="validation-spacer"/>
                    )}
                    <div className={cx("validation-label", {validated: validation.secretAnswerLength})} xd-qa-type="text" xd-qa-name="Answers must be at least 6 characters #1">Answers must be at least 6 characters</div>
                </div>
            </div>
            
            <div className="question-card">
                <div className="label" xd-qa-type="label" xd-qa-name="Security Question #2">Security Question #2</div>
                {QuestionSelect({inputProps:{
                    tabIndex: 103,
                    value: scopedState(state, ['inputs', "secretQuestion2"]).value,
                    isSelected: (i) => i.value === scopedState(state, ['inputs', "secretQuestion2"]).value,
                    onSelect: (i) => {
                        scopedState(state, ['inputs', "secretQuestion2"]).onChange(i?.value ?? "");
                        scopedState(state, ['errors', "secretQuestion2"]).onChange(false);
                    },
                    xdQaName: "Security Questions #2",
                }})}
                {state.value.errors.secretQuestion2 && (
                    <div className="errorMessage">{state.value.errors.secretQuestion2}</div>
                )}
                {PasswordInput({inputProps:{
                    tabIndex: 104,
                    placeholder: "Answer",
                    value: scopedState(state, ['inputs', "secretAnswer2"]).value, 
                    onChange: (e) => {
                        let value = e.target.value; 
                        scopedState(state, ['inputs', "secretAnswer2"]).onChange(value);
                        scopedState(state, ['errors', "secretAnswer2"]).onChange(false);
                    },
                    className: "answer",
                    maxLength: 50,
                    "xd-qa-type": "textbox",
                    "xd-qa-name": "Security Answer #2",
                    autoComplete: "new-password",
                }})}
                {state.value.errors.secretAnswer2 && (
                    <div className="errorMessage">{state.value.errors.secretAnswer2}</div>
                )}
                <div className="validation-line">
                    {validation.questionsDoNotMatch ? (
                        <div className="validation-icon"><FontAwesomeIcon className={"icon"} icon={faCheck} xd-qa-type="icon" xd-qa-name="Questions must not be the same check icon"/></div>
                    ) : (
                        <div className="validation-spacer"/>
                    )}
                    <div className={cx("validation-label", {validated: validation.questionsDoNotMatch})} xd-qa-type="text" xd-qa-name="Questions must not be the same">Questions must not be the same</div>
                </div>
                <div className="validation-line">
                    {validation.answersDoNotMatch ? (
                        <div className="validation-icon"><FontAwesomeIcon className={"icon"} icon={faCheck} xd-qa-type="icon" xd-qa-name="Answers must not be the same check icon"/></div>
                    ) : (
                        <div className="validation-spacer"/>
                    )}
                    <div className={cx("validation-label", {validated: validation.answersDoNotMatch}) } xd-qa-type="text" xd-qa-name="Answers must not be the same">Answers must not be the same</div>
                </div>
                <div className="validation-line">
                    {validation.secretAnswer2Length ? (
                        <div className="validation-icon"><FontAwesomeIcon className={"icon"} icon={faCheck} xd-qa-type="icon" xd-qa-name="Answers must be at least 6 characters check icon #2"/></div>
                    ) : (
                        <div className="validation-spacer"/>
                    )}
                    <div className={cx("validation-label", {validated: validation.secretAnswer2Length})} xd-qa-type="text" xd-qa-name="Answers must be at least 6 characters #2">Answers must be at least 6 characters</div>
                </div>
            </div>
        </div>
    )
);

const QuestionSelect = ({inputProps}) => Select2({
    ...inputProps,
    getLabel: (i) => i.label,
    placeholder: "Select...",
    "xd-qa-type":"dropdown",
    className: "question",
    list: securityQuestions,
});

const securityQuestions = [
    {
        label: 'In what city was your first job located?',
        value: 'In what city was your first job located?',
    },
    {
        label: "What is the first name of your mother's mother?",
        value: "What is the first name of your mother's mother?",
    },
    {
        label: "What was your high school mascot?",
        value: "What was your high school mascot?",
    },
    {
        label: "In what city were you born?",
        value: "In what city were you born?",
    },
    {
        label: "What was the name of your first pet?",
        value: "What was the name of your first pet?",
    },
    {
        label: "What is your father's middle name?",
        value: "What is your father's middle name?",
    },
    {
        label: "What is your mother's maiden name?",
        value: "What is your mother's maiden name?",
    },
    {
        label: "In what city did you attend elementary school?",
        value: "In what city did you attend elementary school?",
    },
    {
        label: "What is the first name of your childhood best friend?",
        value: "What is the first name of your childhood best friend?",
    },
    {
        label: "What is the name of your company?",
        value: "What is the name of your company?",
    },
    {
        label: "In what city is your primary office?",
        value: "In what city is your primary office?",
    },
    {
        label: "What is your primary industry?",
        value: "What is your primary industry?",
    },
];

const defaultState = ({userProfile}) => { 
    return {
        inputs: {
            secretQuestion: securityQuestionExists({secretQuestion: userProfile.value.secretQuestion}) ? userProfile.value.secretQuestion : '',
            secretAnswer: '',
            secretQuestion2: securityQuestionExists({secretQuestion: userProfile.value.secretQuestion2}) ? userProfile.value.secretQuestion2 : '',
            secretAnswer2: '',
        },
        errors: {
            secretQuestion: false,
            secretAnswer: false,
            secretQuestion2: false,
            secretAnswer2: false,
        },
    }
};

const questionsMatch = ({state}) => {
    const question1 = scopedState(state, ['inputs', "secretQuestion"]).value;
    const question2 = scopedState(state, ['inputs', "secretQuestion2"]).value;
    return !question1 || !question2 || question1 === question2;
}

const answersMatch = ({state}) => {
    const answer1 = scopedState(state, ['inputs', "secretAnswer"]).value;
    const answer2 = scopedState(state, ['inputs', "secretAnswer2"]).value;
    return !answer1 || !answer2 || answer1 === answer2;
}

const missingData = ({state}) => {
    return !scopedState(state, ['inputs', "secretQuestion"]).value || 
    !scopedState(state, ['inputs', "secretAnswer"]).value ||
    !scopedState(state, ['inputs', "secretQuestion2"]).value ||
    !scopedState(state, ['inputs', "secretAnswer2"]).value ||
    !!scopedState(state, ['errors', "secretQuestion"]).value ||
    !!scopedState(state, ['errors', "secretAnswer"]).value ||
    !!scopedState(state, ['errors', "secretQuestion2"]).value ||
    !!scopedState(state, ['errors', "secretAnswer2"]).value
}

const securityQuestionExists = ({secretQuestion}) => {
    return !!securityQuestions.find((q) => q.value === secretQuestion)
}
